// ============================================================================
// LA SAISIE EN RAFALE — sélection au clic, et retour chariot qui avance.
//
// ⚠️ POURQUOI C'EST UN HÔTE DÉLÉGUÉ ET NON UNE PROP SUR CHAQUE CHAMP : le back-office compte des
// centaines de champs, et un comportement qu'il faut penser à câbler ne sera câblé qu'aux endroits
// dont on se souvient. Un seul écouteur au document, et la règle vaut partout — y compris sur les
// écrans qui n'existent pas encore.
//
// ⚠️ LA DOUCHETTE EST LE VRAI COMMANDITAIRE. Elle tape le code puis envoie un RETOUR CHARIOT, ou une
// TABULATION selon sa configuration — et l'opérateur n'a pas la main dessus au moment où il scanne une
// série. Les deux touches doivent donc faire la même chose : passer au champ suivant. La tabulation le
// fait déjà nativement ; c'est le retour chariot qui manquait.
//
// ⚠️ ET LA SÉLECTION AU CLIC est ce qui rend le remplacement possible : sur un champ déjà rempli, un
// scan qui ne remplace pas CONCATÈNE — on obtient un code à trente chiffres qui ne correspond à rien,
// et l'erreur ne se voit qu'à l'enregistrement.
// ============================================================================
import { useEffect } from 'react';

/**
 * Marque une zone de saisie en rafale. Le retour chariot y passe au champ suivant.
 * ⚠️ PAS GLOBAL, ET C'EST DÉLIBÉRÉ : ailleurs, « Entrée » valide un formulaire. En faire une touche de
 * navigation partout casserait la connexion, la recherche et toutes les modales de confirmation.
 */
export const SCAN_ZONE_ATTR = 'data-scan-zone';

/** Les types de champ où la sélection au focus a un sens — jamais une case à cocher ni un curseur. */
const TYPES_TEXTE = new Set(['text', 'search', 'tel', 'url', 'email', 'number', 'password', '']);

function estChampTexte(el: EventTarget | null): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.disabled || el.readOnly) return false;
  return TYPES_TEXTE.has(el.type);
}

/** Les champs de la zone, dans l'ordre du DOM — celui dans lequel l'œil et la douchette progressent. */
function champsDeLaZone(depuis: HTMLElement): HTMLInputElement[] {
  const zone = depuis.closest<HTMLElement>(`[${SCAN_ZONE_ATTR}]`);
  if (!zone) return [];
  return Array.from(zone.querySelectorAll<HTMLInputElement>('input'))
    .filter((i) => !i.disabled && !i.readOnly && i.type !== 'hidden');
}

/**
 * Monte les deux comportements. À poser UNE fois près de la racine, avec les autres hôtes UI.
 *
 * ⚠️ LA SÉLECTION SE FAIT AU `focus`, PAS AU `click` : au clic, le navigateur pose ensuite le curseur
 * et défait la sélection. Et parce qu'un clic déclenche aussi un `mouseup` qui repositionne le curseur,
 * on annule ce seul `mouseup`-là — sans quoi la sélection clignote et disparaît.
 * ⚠️ RE-CLIQUER DANS UN CHAMP DÉJÀ FOCALISÉ NE RE-SÉLECTIONNE PAS : c'est le geste de quelqu'un qui
 * veut placer son curseur pour corriger un caractère. Lui resélectionner tout l'en empêcherait.
 */
export function FieldNavHost() {
  useEffect(() => {
    let selectionEnCours = false;

    const surFocus = (e: FocusEvent) => {
      const el = e.target;
      if (!estChampTexte(el)) return;
      if (el.value === '') return; // rien à sélectionner : inutile de perturber le curseur
      selectionEnCours = true;
      el.select();
    };

    // Le `mouseup` du clic qui vient de donner le focus — et lui seul — est neutralisé.
    const surMouseUp = (e: MouseEvent) => {
      if (!selectionEnCours) return;
      selectionEnCours = false;
      if (estChampTexte(e.target)) e.preventDefault();
    };
    const surBlur = () => { selectionEnCours = false; };

    /**
     * ⚠️ LE RETOUR CHARIOT AVANCE, IL NE VALIDE PAS — dans une zone de saisie en rafale seulement.
     * ⚠️ ET IL NE FAIT RIEN SUR LE DERNIER CHAMP : boucler sur le premier ferait recommencer la série
     * en écrasant ce qu'on vient de scanner, sans que rien ne l'annonce.
     */
    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      // ⚠️ UN CHAMP QUI GÈRE DÉJÀ « Entrée » GARDE LA MAIN : le geste de la matrice des déclinaisons
      // valide sa ligne AVANT d'avancer. Le doubler ici sauterait un champ sur deux.
      if (e.defaultPrevented) return;
      const champs = champsDeLaZone(el);
      const i = champs.indexOf(el);
      if (i < 0) return;
      e.preventDefault();
      champs[i + 1]?.focus();
    };

    document.addEventListener('focusin', surFocus);
    document.addEventListener('mouseup', surMouseUp, true);
    document.addEventListener('focusout', surBlur);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('focusin', surFocus);
      document.removeEventListener('mouseup', surMouseUp, true);
      document.removeEventListener('focusout', surBlur);
      document.removeEventListener('keydown', surTouche);
    };
  }, []);

  return null;
}
